import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Send } from 'lucide-react';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = `mailto:chinesefoodmapsapp@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`)}`;
    window.location.href = mailtoLink;
    setSubmitted(true);
  };

  return (
    <>
      <Helmet>
        <title>Contact Chinese Food Maps - Get in Touch | Chinese Food Maps</title>
        <meta name="description" content="Have questions about Chinese cuisine or want to suggest a restaurant? Contact Chinese Food Maps! Whether you're recommending a hidden gem, need help finding specific dishes, or want to share your Chinese food experiences, we're here to help." />
        <meta property="og:title" content="Contact Chinese Food Maps - Get in Touch | Chinese Food Maps" />
        <meta property="og:description" content="Have questions about Chinese cuisine or want to suggest a restaurant? Contact Chinese Food Maps!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chinesefoodmaps.com/contact" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Chinese Food Maps - Get in Touch | Chinese Food Maps" />
        <meta name="twitter:description" content="Have questions about Chinese cuisine or want to suggest a restaurant? Contact Chinese Food Maps!" />
        <meta name="twitter:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/contact" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Let's Chat About Chinese Food!</h1>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-4 text-red-800">Drop Us a Line</h2>
            
            <div className="mb-6">
              <p className="text-lg text-gray-600 mb-6">
                Got a Chinese restaurant we should know about? Found a hidden dim sum spot? Just want to talk about regional Chinese cuisines? We're all ears! Whether you're a food enthusiast, restaurant owner, or just someone passionate about authentic Chinese food, we'd love to hear from you.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <Mail size={24} className="text-red-700 mr-3" />
                  <a href="mailto:chinesefoodmapsapp@gmail.com" className="text-lg text-gray-700 hover:text-red-700 transition-colors">
                    chinesefoodmapsapp@gmail.com
                  </a>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-xl mb-2 text-red-700">When We're Around</h3>
              <p className="text-lg text-gray-700">Monday - Friday: 9AM - 5PM EST</p>
              <p className="text-lg text-gray-700">Saturday - Sunday: Closed (We're out exploring Chinese restaurants!)</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-4 text-red-800">Share Your Thoughts</h2>
            
            {submitted ? (
              <div className="text-center py-8">
                <h3 className="text-xl font-semibold text-green-600 mb-4">谢谢 (Thank You)!</h3>
                <p className="text-gray-600">Your email client should be opening to send us a message.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-6 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2 text-lg">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-lg"
                    placeholder="Food Enthusiast"
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2 text-lg">
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-lg"
                    placeholder="you@example.com"
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-gray-700 font-medium mb-2 text-lg">
                    What's This About?
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-lg"
                  >
                    <option value="">Choose a topic...</option>
                    <option value="Suggest a Restaurant">Suggest a Chinese Restaurant</option>
                    <option value="Report Missing Restaurant">Report Missing Restaurant</option>
                    <option value="Regional Cuisine Question">Regional Cuisine Question</option>
                    <option value="Recipe Inquiry">Recipe Inquiry</option>
                    <option value="Share Your Experience">Share Your Chinese Food Experience</option>
                    <option value="Something Else">Something Else</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2 text-lg">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-lg"
                    placeholder="Tell us about your favorite Chinese dishes, recommend a restaurant, or ask about regional cuisines..."
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  className="w-full py-3 px-6 bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors flex items-center justify-center text-lg font-medium"
                  disabled={isSubmitting}
                >
                  <Send size={22} className="mr-2" />
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};